import forEach from 'lodash/forEach';
import first from 'lodash/first';
import last from 'lodash/last';
import map from 'lodash/map';
import partition from 'lodash/partition';

import { TYPE, SEATRADIUS, TABLE, SEAT } from '@/constants';
import { Table, TableModelparams } from './table';
import { TableSeat } from './tableSeat';

export default class RectangularTable extends Table {
  constructor(params: TableModelparams) {
    super(params);
    this.initView();
    this.render();
  }

  get subtype() {
    return TYPE.TABLE_RECTANGULAR;
  }

  get x() {
    return this.cx - (this.attributes.width / 2) * this.app.seatmap.scale;
  }

  get y() {
    return this.cy - (this.attributes.height / 2) * this.app.seatmap.scale;
  }

  get cx() {
    return this.attributes.position[0];
  }

  get cy() {
    return this.attributes.position[1];
  }

  get seatsDirection() {
    return this.attributes.seatsDirection;
  }

  set seatsDirection(value) {
    this.setAttribute('seatsDirection', value);
    this.render();
  }

  changeWidth(width: number) {
    this.setAttribute('autoWidth', false);
    this.setAttribute('width', width);
    this.render();
  }

  changeHeight(height: number) {
    this.setAttribute('autoWidth', false);
    this.setAttribute('height', height);
    this.render();
  }

  initView() {
    const { width, height, position } = this.attributes;
    const [cx, cy] = position;
    this.seatsGroup = this.canvas.group().addClass('table-seats');
    this.label = this.createLabel();
    this.shape = this.canvas
      .rect(width * this.app.seatmap.scale, height * this.app.seatmap.scale)
      .addClass('table-shape');
    this.shape.center(cx, cy);
    forEach(this.seats, (seat) => {
      this.seatsGroup.add(seat.view);
    });
    this.view.addClass('table-rectangular');
    this.view.add(this.shape).add(this.seatsGroup).add(this.label);
    this.label.attr({ x: this.cx, y: this.cy });
    this.label.front();
    if (!this.app.showLabels) this.hideLabel();
    if (this.seatsCount) this.renderSeats();
    this.view.transform({ rotation: this.attributes.rotation });
  }

  render() {
    const { autoWidth } = this.attributes;
    if (autoWidth) this.adjustWidth();
    this.shape
      .size(this.attributes.width * this.app.seatmap.scale, this.attributes.height * this.app.seatmap.scale)
      .center(this.cx, this.cy);
    if (this.seatsCount) this.renderSeats();
    this.view.untransform().transform({ rotation: this.attributes.rotation });
  }

  adjustWidth() {
    let value;
    if (this.seatsDirection === TABLE.RECTANGULAR.DIRECTION.ONESIDE) {
      value = TABLE.RECTANGULAR.AUTO_WIDTH_COEF * this.seatsCount;
    } else if (this.seatsDirection === TABLE.RECTANGULAR.DIRECTION.TWOSIDES) {
      const k = this.seatsCount % 2 === 0 ? this.seatsCount / 2 : (this.seatsCount + 1) / 2;
      value = TABLE.RECTANGULAR.AUTO_WIDTH_COEF * k;
    } else {
      const k = this.seatsCount % 2 === 0 ? this.seatsCount / 2 - 1 : (this.seatsCount - 1) / 2;
      value = TABLE.RECTANGULAR.AUTO_WIDTH_COEF * k;
    }
    if (value < TABLE.RECTANGULAR.AUTO_WIDTH_COEF) value = TABLE.RECTANGULAR.AUTO_WIDTH_COEF;
    this.setAttribute('width', value);
  }

  renderSeats() {
    switch (this.seatsDirection) {
      case TABLE.RECTANGULAR.DIRECTION.ONESIDE:
        this.renderOneSideSeats(this.seats);
        break;
      case TABLE.RECTANGULAR.DIRECTION.FOURSIDES:
        this.renderFourSidesSeats(this.seats);
        break;
      case TABLE.RECTANGULAR.DIRECTION.TWOSIDES:
      default:
        this.renderTwoSidesSeats(this.seats);
        break;
    }
  }

  renderOneSideSeats(seats: TableSeat[]) {
    const coef = this.attributes.width / seats.length;
    const dy = this.y - SEATRADIUS * this.app.seatmap.scale;
    this.renderSeatsWithCoefs(seats, coef, dy);
  }

  renderTwoSidesSeats(seats: TableSeat[]) {
    let bottomSeatsLength;
    let bottomLengthCoef;
    const isEven = seats.length % 2 === 0;
    const [topSeats, bottomSeats] = this.divideSeatsByTwoSides(seats);
    const topSeatslength = topSeats.length;
    const topLengthCoef = this.attributes.width / topSeatslength;
    if (isEven) {
      bottomLengthCoef = topLengthCoef;
    } else {
      bottomSeatsLength = bottomSeats.length;
      bottomLengthCoef = this.attributes.width / bottomSeatsLength;
    }
    const tdy = this.y - SEATRADIUS * this.app.seatmap.scale;
    const bdy = this.y + (this.attributes.height + SEATRADIUS) * this.app.seatmap.scale;
    this.renderSeatsWithCoefs(topSeats, topLengthCoef, tdy);
    this.renderSeatsWithCoefs(bottomSeats, bottomLengthCoef, bdy);
  }

  renderFourSidesSeats(seats: TableSeat[]) {
    const len = seats.length;
    if (len === 1) {
      this.renderSeatsBySides(first(seats)!, TABLE.RECTANGULAR.SIDE.LEFT);
    } else if (len === 2) {
      this.renderSeatsBySides(first(seats)!, TABLE.RECTANGULAR.SIDE.LEFT);
      this.renderSeatsBySides(last(seats)!, TABLE.RECTANGULAR.SIDE.RIGHT);
    } else {
      this.renderSeatsBySides(seats[0], TABLE.RECTANGULAR.SIDE.LEFT);
      this.renderSeatsBySides(seats[1], TABLE.RECTANGULAR.SIDE.RIGHT);
      this.renderTwoSidesSeats(seats.slice(2));
    }
  }

  renderSeatsWithCoefs(seats: TableSeat[], coef: number, y: number) {
    const scaledCoef = coef * this.app.seatmap.scale;
    const halfCoef = scaledCoef / 2;
    forEach(seats, (seat, i) => {
      const x = this.x + halfCoef + scaledCoef * i;
      seat.attributes.position = [x, y];
      seat.redraw();
    });
  }

  renderSeatsBySides(seat: TableSeat, side: 1 | 2 | 3 | 4) {
    const x =
      side === TABLE.RECTANGULAR.SIDE.LEFT
        ? this.x - SEATRADIUS * this.app.seatmap.scale
        : this.x + (this.attributes.width + SEATRADIUS) * this.app.seatmap.scale;
    seat.attributes.position = [x, this.cy];
    seat.redraw();
  }

  divideSeatsByTwoSides(seats: TableSeat[]) {
    return partition(seats, (seat) => seat.attributes.orderid % 2 === 0);
  }

  // FIXME: What is ts = tableJson used for here?
  clone(options: TableModelparams['options'] = {}) {
    const { keepPosition, keepLabel, ts } = options;
    const tableJson = this.toJSON(keepLabel);
    if (!keepPosition) {
      const [x, y] = tableJson.attributes.position;
      tableJson.attributes.position = [x + 50, y + 50];
    }
    if (!keepLabel) delete tableJson.attributes.label;
    tableJson.uuid = this.getUniqueId();
    if (ts) tableJson.options = options;
    return RectangularTable.fromJSON(tableJson, true);
  }

  static fromJSON(params: TableModelparams, clone = false) {
    if (params.seats && clone) {
      params.seats = map(params.seats, (seatParams) => TableSeat.clone(seatParams, params.options));
    } else if (params.seats) {
      params.seats = map(params.seats, (seatParams) => TableSeat.fromJSON(seatParams));
    }
    params.attributes.label =
      params.attributes.label !== undefined && params.attributes.label !== '' ? params.attributes.label : SEAT.LABEL;
    return RectangularTable.create(params);
  }

  static create(params: TableModelparams) {
    const attributes = {
      label: SEAT.LABEL,
      width: TABLE.RECTANGULAR.WIDTH,
      height: TABLE.RECTANGULAR.HEIGHT,
      seatsDirection: TABLE.RECTANGULAR.DIRECTION.TWOSIDES,
      position: [0, 0],
      rotation: 0,
      autoWidth: true,
      isGrouped: false,
    };
    params.attributes = Object.assign({}, attributes, params.attributes);
    return new RectangularTable(params);
  }

  onScaleChange() {
    const { width, height, position } = this.attributes;
    this.shape.size(width * this.app.seatmap.scale, height * this.app.seatmap.scale).center(position[0], position[1]);
    this.renderSeats();
  }
}
